import { useState, useEffect } from 'react';
import clientService from '../services/clientService';

export const useClients = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const { data, error } = await clientService.getAllClients();
      if (error) {
        setError(error);
      } else {
        setClients(data);
        setError(null);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const createClient = async (clientData) => {
    try {
      const { data, error } = await clientService.createClient(clientData);
      if (error) {
        setError(error);
        return { success: false, error };
      }
      
      setClients(prev => [data, ...prev]);
      return { success: true, data };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  const updateClient = async (id, clientData) => {
    try {
      const { data, error } = await clientService.updateClient(id, clientData);
      if (error) {
        setError(error);
        return { success: false, error };
      }
      
      setClients(prev => prev.map(client => client.id === id ? data : client));
      return { success: true, data };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  const deleteClient = async (id) => {
    try {
      const { data, error } = await clientService.deleteClient(id);
      if (error) {
        setError(error);
        return { success: false, error };
      }
      
      setClients(prev => prev.filter(client => client.id !== id));
      return { success: true, data };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  return {
    clients,
    loading,
    error,
    refetch: fetchClients,
    createClient,
    updateClient,
    deleteClient
  };
};

export const useClientSegmentation = (clients) => {
  const [segmentationData, setSegmentationData] = useState({
    serviceType: {},
    billingFrequency: {},
    gstType: {},
    paymentStatus: {},
    accountManager: {},
    revenueByService: {}
  });

  useEffect(() => {
    if (clients.length > 0) {
      const data = {
        serviceType: clients.reduce((acc, client) => {
          const service = client.segmentation?.service_type || 'Unassigned';
          acc[service] = (acc[service] || 0) + 1;
          return acc;
        }, {}),
        
        billingFrequency: clients.reduce((acc, client) => {
          const frequency = client.segmentation?.billing_frequency || 'Monthly';
          acc[frequency] = (acc[frequency] || 0) + 1;
          return acc;
        }, {}),
        
        gstType: clients.reduce((acc, client) => {
          const type = client.gst_type || 'B2B';
          acc[type] = (acc[type] || 0) + 1;
          return acc;
        }, {}),
        
        paymentStatus: clients.reduce((acc, client) => {
          const status = client.segmentation?.payment_status || 'Active';
          acc[status] = (acc[status] || 0) + 1;
          return acc;
        }, {}),
        
        accountManager: clients.reduce((acc, client) => {
          const manager = client.segmentation?.account_manager || 'Unassigned';
          acc[manager] = (acc[manager] || 0) + 1;
          return acc;
        }, {}),
        
        revenueByService: clients.reduce((acc, client) => {
          const service = client.segmentation?.service_type || 'Unassigned';
          const revenue = client.monthly_revenue || 0;
          acc[service] = (acc[service] || 0) + revenue;
          return acc;
        }, {})
      };
      
      setSegmentationData(data);
    }
  }, [clients]);

  return segmentationData;
};